/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { includes, isNil } from 'lodash';
import {
  FormulaParseError,
  FormulaParseErrorType,
  FormulaRewriteResult,
  FormulaValidationResult,
} from './formula-errors';

import { CharStream, FormulaLexer, formulaLexer } from './formula-lexer';
import { FormulaFunction, FormulaNameResolver } from './formula-name-resolver';
import { FormulaRewriteStrategy } from './formula-rewrite-strategy';
import { FormulaToken, FormulaTokenType } from './formula-token';

function buildSuccessValidationResult(): FormulaValidationResult {
  return { valid: true };
}

function buildErrorValidationResult(error: FormulaParseError): FormulaValidationResult {
  return {
    valid: false,
    errorId: error.errorId,
    errorParams: error.errorParams,
    position: error.position,
  };
}

/**
 * This parser defines the following grammar rules:
 *
 * root                -> expression EOL |
 *                        EOL
 * expression          -> plus
 * plus                -> minus + plus
 * minus               -> mul - minus
 * mul                 -> div * mul
 * div                 -> mod / div
 * mod                 -> unary-expression MOD mod
 * unary-expression    -> '-' simple-expression |
 *                        '+' simple-expression |
 *                        simple-expression
 * simple-expression   -> ID |
 *                        NUMBER |
 *                        '(' expression ')' |
 *                        function-call
 * function-call       -> ID '(' function-parameters ')' |
 *                        ID '(' ')'
 * function-parameters -> expression ',' function-parameters |
 *                        expression
 *
 */
export class FormulaParser {
  private lexer: FormulaLexer;
  private rewriteStrategy?: FormulaRewriteStrategy;
  private rewriteBuffer: string[] = [];

  constructor(private nameResolver: FormulaNameResolver) {
  }

  validate(chars: CharStream): FormulaValidationResult {
    // try/catch is ugly, but it dramatically simplifies control flow here
    try {
      this.parse(chars);
      return buildSuccessValidationResult();
    } catch (error) {
      return buildErrorValidationResult(error);
    }
  }

  rewrite(chars: CharStream, strategy: FormulaRewriteStrategy): FormulaRewriteResult {
    // try/catch is ugly, but it dramatically simplifies control flow here
    const rewriteBuffer: string[] = [];
    try {
      this.rewriteBuffer = rewriteBuffer;
      this.rewriteStrategy = strategy;
      this.parse(chars);
    } catch (error) {
      return buildErrorValidationResult(error);
    } finally {
      this.rewriteStrategy = void 0;
      this.rewriteBuffer = [];
    }

    return { ...buildSuccessValidationResult(), rewritten: rewriteBuffer.join('') };
  }

  private parse(chars: CharStream): void {
    this.lexer = formulaLexer(chars);

    const type = this.lookaheadType();

    if (type === FormulaTokenType.EOL) {
      this.next(FormulaTokenType.EOL);
    } else {
      this.parseExpression();
      this.next(FormulaTokenType.EOL, FormulaParseErrorType.UNEXPECTED_TOKEN);
    }
  }

  private parseExpression(): void {
    this.parsePlus();
  }

  private parsePlus(): void {
    this.parseBinaryExpression(FormulaTokenType.OP_PLUS, this.parseMinus);
  }

  private parseMinus(): void {
    this.parseBinaryExpression(FormulaTokenType.OP_MINUS, this.parseMul);
  }

  private parseMul(): void {
    this.parseBinaryExpression(FormulaTokenType.OP_MUL, this.parseDiv);
  }

  private parseDiv(): void {
    this.parseBinaryExpression(FormulaTokenType.OP_DIV, this.parsePow);
  }

  private parsePow(): void {
    this.parseBinaryExpression(FormulaTokenType.OP_POW, this.parseUnaryExpression);
  }

  private parseBinaryExpression(type: FormulaTokenType, nextParseRule: (this: FormulaParser) => void): void {
    while (true) {
      nextParseRule.call(this);

      if (this.lookaheadType() !== type) {
        break;
      }
      this.next(type);
    }
  }

  private parseUnaryExpression(): void {
    const type = this.lookaheadType();

    switch (type) {
      case FormulaTokenType.OP_PLUS:
      case FormulaTokenType.OP_MINUS:
        this.next(type);
        this.parseSimpleExpression();
        break;
      default:
        this.parseSimpleExpression();
        break;
    }
  }

  private parseSimpleExpression(): void {
    const type = this.lookaheadType();

    switch (type) {
      case FormulaTokenType.BRACKET_OPENED:
        this.next(FormulaTokenType.BRACKET_OPENED);
        this.parseExpression();
        this.next(FormulaTokenType.BRACKET_CLOSED, FormulaParseErrorType.EXPECTED_CLOSING_BRACKET);
        break;
      case FormulaTokenType.NUMBER:
        this.next(FormulaTokenType.NUMBER);
        break;
      case FormulaTokenType.ID: {
        // lookahead second token, this is necessary because "ID" and "ID(" are different things.
        const type2 = this.lookaheadType(2);
        if (type2 === FormulaTokenType.BRACKET_OPENED) {
          this.parseFunctionCall();
        } else {
          this.parseVariableName();
        }
        break;
      }
      default:
        this.raiseError(this.lookahead(), FormulaParseErrorType.UNEXPECTED_TOKEN);
        break;
    }
  }

  private parseFunctionCall(): void {
    const nameToken = this.next(FormulaTokenType.ID);
    const func = this.nameResolver.resolveFunction(nameToken.content);

    if (isNil(func)) {
      this.raiseError(nameToken, FormulaParseErrorType.UNKNOWN_FUNCTION_NAME);
      return;
    }

    this.next(FormulaTokenType.BRACKET_OPENED);
    let closedBracket; // we save closed bracket to generate correct position for error later, if any

    const type = this.lookaheadType();

    let actualArgCount = 0;
    if (type === FormulaTokenType.BRACKET_CLOSED) {
      closedBracket = this.next(FormulaTokenType.BRACKET_CLOSED);
    } else {
      actualArgCount = this.parseFunctionParameters(func);
      closedBracket = this.next(FormulaTokenType.BRACKET_CLOSED, FormulaParseErrorType.EXPECTED_CLOSING_BRACKET);
    }

    if (!includes(func.allowedArgCount, actualArgCount)) {
      this.raiseError(closedBracket, FormulaParseErrorType.ILLEGAL_NUMBER_OF_ARGUMENTS);
    }
  }

  private parseFunctionParameters(func: FormulaFunction): number {
    let argCount = 0;
    while (true) {
      const type = this.lookaheadType();
      switch (type) {
        case FormulaTokenType.COMMA:
        case FormulaTokenType.BRACKET_CLOSED:
          // this function is called when at least one function argument exists
          this.raiseError(this.lookahead(), FormulaParseErrorType.EXPECTED_FUNCTION_ARGUMENT);
          break;
        default:
          this.parseExpression();
          break;
      }

      argCount++;

      if (this.lookaheadType() !== FormulaTokenType.COMMA) {
        break;
      }

      // raise error when one more argument is too much for this function
      const comma = this.next(FormulaTokenType.COMMA);
      if (argCount === func.maxArgCount) {
        this.raiseError(comma, FormulaParseErrorType.ILLEGAL_NUMBER_OF_ARGUMENTS);
      }
    }

    return argCount;
  }

  private parseVariableName(): void {
    const nameToken = this.next(FormulaTokenType.ID);

    const variable = this.nameResolver.resolveVariable(nameToken.content);

    if (isNil(variable)) {
      this.raiseError(nameToken, FormulaParseErrorType.UNKNOWN_VARIABLE_NAME);
      return;
    }

    if (this.rewriteStrategy) {
      this.rewriteLast(this.rewriteStrategy.rewriteVariableName(nameToken, variable));
    }
  }

  private next(type: FormulaTokenType, errorId?: string): FormulaToken {
    this.ignoreSpace();

    const token = this.lexer.read();

    if (isNil(token) || token.type !== type) {
      this.raiseError(token, errorId);
    }

    if (this.rewriteStrategy) {
      this.writeNext(this.rewriteStrategy.rewriteOtherToken(<FormulaToken>token));
    }

    return <FormulaToken>token;
  }

  private lookahead(steps: number = 1): FormulaToken {
    this.ignoreSpace();

    const buffered: any[] = [];
    for (let i = steps - 2; i >= 0; i--) {
      buffered.push(this.lexer.read());
    }

    const token = this.lexer.read();

    this.lexer.unread(token);

    if (isNil(token)) {
      throw new Error('Reading should end on EOL token and should not retrieve undefined token from lexer');
    }

    while (buffered.length) {
      this.lexer.unread(buffered.pop());
    }

    return token;
  }

  private lookaheadType(steps: number = 1): FormulaTokenType {
    return this.lookahead(steps).type;
  }

  private ignoreSpace(): void {
    let token;
    do {
      token = this.lexer.read();

      if (isNil(token)) {
        throw new Error('Reading should end on EOL token and should not retrieve undefined token from lexer');
      }

      if (token.type === FormulaTokenType.SPACE && this.rewriteStrategy) {
        this.writeNext(this.rewriteStrategy.rewriteOtherToken(token));
      }
    } while (token.type === FormulaTokenType.SPACE);
    this.lexer.unread(token);
  }

  private raiseError(token?: FormulaToken, errorId?: string): void {
    if (token) {
      throw new FormulaParseError(errorId || FormulaParseErrorType.UNEXPECTED, { text: token.content }, token.start);
    } else {
      throw new FormulaParseError(errorId || FormulaParseErrorType.UNEXPECTED);
    }
  }

  private writeNext(text: string): void {
    this.rewriteBuffer.push(text);
  }

  private rewriteLast(text: string): void {
    this.rewriteBuffer[this.rewriteBuffer.length - 1] = text;
  }
}
